import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../lib/session';
import { api, type TicketList, type ViewId } from '../lib/api';
import { ProductSwitcher } from './ProductSwitcher';
import { GearIcon, UserMenu } from './UserMenu';

export const VIEWS: readonly ViewId[] = [
  'open', 'mine', 'unassigned', 'myGroup', 'atRisk', 'toEstimate', 'toQuote', 'awaitingApproval', 'waitingCustomer', 'waitingThirdParty', 'recentlyResolved'
];
export type { ViewId };

/** Conteos de las vistas (la misma consulta que la bandeja: React Query la comparte). */
export function useViewCounts() {
  return useQuery({
    queryKey: ['tickets', 'open', {}],
    queryFn: () => api<TicketList>('/tickets?view=open'),
    select: d => ({ ...d.counts, mailTriage: d.mailTriage ?? 0 }),
    staleTime: 15_000
  }).data;
}

export function Shell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const location = useLocation();
  const counts = useViewCounts();
  const navigate = useNavigate();
  const urlQuery = location.pathname === '/' ? (new URLSearchParams(location.search).get('q') ?? '') : '';
  const [search, setSearch] = useState(urlQuery);
  useEffect(() => { setSearch(urlQuery); }, [urlQuery]);
  const currentView = location.pathname === '/' && !new URLSearchParams(location.search).get('q')
    ? (new URLSearchParams(location.search).get('view') ?? 'open')
    : null;

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <header className="flex flex-wrap items-center gap-3 bg-ink px-4 py-3 text-paper print:hidden sm:gap-4 sm:px-6">
        <div className="mr-2 flex items-center gap-4">
          <ProductSwitcher />
          {me?.tenantLogo ? (
            <span className="hidden h-10 items-center border-l border-[#3A3936] pl-4 sm:flex">
              <span className="flex h-10 w-[168px] items-center justify-center rounded-md bg-white px-3 py-1.5">
                <img src={me.tenantLogo} alt={me.tenantName} title={me.tenantName} className="max-h-7 max-w-full object-contain" />
              </span>
            </span>
          ) : (
            <span className="hidden border-l border-[#3A3936] py-1.5 pl-4 text-sm text-[#C9C7C2] sm:inline">{me?.tenantName}</span>
          )}
        </div>
        <form role="search" className="order-last flex min-w-0 flex-[1_1_100%] md:order-none md:flex-[1_1_320px]"
          onSubmit={e => {
            e.preventDefault();
            const q = search.trim();
            navigate(q ? `/?${new URLSearchParams({ q })}` : '/');
          }}>
          <label htmlFor="global-search" className="sr-only">{t('shell.search')}</label>
          <input id="global-search" type="search" placeholder={t('shell.search')} value={search} onChange={e => setSearch(e.target.value)}
            className="h-10 w-full min-w-0 rounded-md border border-[#3A3936] bg-[#2A2926] px-3 text-sm text-paper placeholder:text-[#A8A5A0] focus:border-accent-bright focus:outline-none" />
        </form>
        <Link to="/tickets/new"
          className="inline-flex h-10 items-center gap-2 rounded-md bg-accent-bright px-4 text-sm font-semibold text-[#10302D] hover:bg-[#9BDDD5]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          {t('shell.newTicket')}
        </Link>
        <div className="ml-auto flex items-center gap-2 text-sm">
          {me?.deskRole === 'admin' && (
            <Link to="/config" title={`${t('shell.settings')} de ${me?.tenantName ?? ''}`} aria-label={t('shell.settings')}
              aria-current={location.pathname === '/config' ? 'page' : undefined}
              className={`grid h-9 w-9 place-items-center rounded-full ${location.pathname === '/config' ? 'bg-[#2A2926] text-accent-bright' : 'text-[#C9C7C2] hover:bg-[#2A2926] hover:text-paper'}`}>
              <GearIcon />
            </Link>
          )}
          <UserMenu />
        </div>
      </header>

      <div className="flex flex-1 flex-wrap items-stretch">
        <nav aria-label={t('shell.views')} className="box-border print:hidden flex max-w-full flex-[1_1_240px] flex-col gap-1 border-r border-line bg-white px-3 py-5">
          <div className="px-2.5 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{t('shell.views')}</div>
          {VIEWS.filter(view => view !== 'toQuote' || me?.deskRole === 'admin').map(view => {
            const active = currentView === view;
            return (
              <Link key={view} to={view === 'open' ? '/' : `/?view=${view}`} aria-current={active ? 'page' : undefined}
                className={`flex items-center justify-between rounded-md px-2.5 py-2 text-sm ${
                  active ? 'bg-accent-soft font-semibold text-accent' : 'text-[#3F3D39] hover:bg-paper'
                }`}>
                <span>{t(`views.${view}`)}</span>
                <span className={`font-mono text-xs ${view === 'atRisk' && counts?.atRisk ? 'rounded-full bg-[#B91C1C] px-1.5 text-white' : view === 'toQuote' && counts?.toQuote ? 'rounded-full bg-[#C2410C] px-1.5 text-white' : ''}`}>{counts?.[view] ?? '·'}</span>
              </Link>
            );
          })}
          {me?.modules?.change && (
            <Link to="/cambios" aria-current={location.pathname.startsWith('/cambios') ? 'page' : undefined}
              className={`mt-3 flex items-center justify-between rounded-md border-t border-[#EFEDE9] px-2.5 py-2 pt-3 text-sm ${
                location.pathname.startsWith('/cambios') ? 'bg-accent-soft font-semibold text-accent' : 'text-[#3F3D39] hover:bg-paper'}`}>
              <span className="inline-flex items-center gap-2">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 7h11l-3-3M20 17H9l3 3" /></svg>
                Cambios
              </span>
              <span className="rounded bg-[#EDEBE7] px-1 text-[10px] font-semibold text-muted">Change</span>
            </Link>
          )}
          {(me?.deskRole === 'admin' || me?.deskRole === 'team_lead' || me?.serviceManager) && (
            <Link to="/contratos" aria-current={location.pathname.startsWith('/contratos') ? 'page' : undefined}
              className={`${me?.modules?.change ? 'mt-1' : 'mt-3 border-t border-[#EFEDE9] pt-3'} flex items-center gap-2 rounded-md px-2.5 py-2 text-sm ${
                location.pathname.startsWith('/contratos') ? 'bg-accent-soft font-semibold text-accent' : 'text-[#3F3D39] hover:bg-paper'}`}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></svg>
              Contratos
            </Link>
          )}
          <Link to="/correo" aria-current={location.pathname === '/correo' ? 'page' : undefined}
            className={`${me?.modules?.change || me?.deskRole === 'admin' || me?.deskRole === 'team_lead' || me?.serviceManager ? 'mt-1' : 'mt-3 border-t border-[#EFEDE9] pt-3'} flex items-center justify-between rounded-md px-2.5 py-2 text-sm ${
              location.pathname === '/correo' ? 'bg-accent-soft font-semibold text-accent' : 'text-[#3F3D39] hover:bg-paper'}`}>
            <span className="inline-flex items-center gap-2">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
              {t('shell.mailTriage')}
            </span>
            <span className={`font-mono text-xs ${counts?.mailTriage ? 'rounded-full bg-[#C2410C] px-1.5 text-white' : ''}`}>{counts?.mailTriage ?? '·'}</span>
          </Link>
        </nav>
        <main className="box-border min-w-0 flex-[999_1_560px] p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
